import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { StoreProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Store } from "assets/SideBarIcons/store.svg";
import StoreTabs from "./StoreTabs";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import { GetStoreByIdResponse } from "subModule/src/services/store/interface";
import Loader from "layout/Loader";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import DynamicSelect from "common/Select/Select";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { pickProfileEntityTitle } from "Helpers/pickProfileEntityTitle";
interface FormData {
  [key: string]: any;
}

interface StoreProps {
  setAddNew?: any;
  refetch?: any;
}
export default function StoreModal(props: StoreProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "stations",
    moduleLabelKey: "stores",
    moduleHref: "/stores",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      inventoryMode: { value: 1, label: "OPEN" },
    },
  });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedStoreNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phStoreNameAr") : t("phStoreNameEn");
  const inventoryModeValue = watch("inventoryMode");
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  const inventoryModeOptions = [
    { value: 1, label: "OPEN" },
    { value: 2, label: "BIN" },
  ];
  useEffect(() => {
    stationsRefetch();
  }, []);
  useEffect(() => {
    if (params?.id && !props?.setAddNew && stationsData) {
      if (loading) getByID();
    }
  }, [params, stationsData]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    StoreProfileService.getStoreByID(+(params as any)?.id)
      .then((res: GetStoreByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            inventoryMode:
              inventoryModeOptions.find(
                (o) => o.value === Number(res?.data?.inventoryMode),
              ) || inventoryModeOptions[0],
            ...(Array.isArray(res?.data?.station) &&
              res?.data?.station?.length && {
                stationId: {
                  value: res?.data?.station[0]?.stationId,
                  label:
                    res?.data?.station[0]?.stationName ||
                    res?.data?.station[0]?.stationId,
                },
              }),
          });
          captureLabel(pickProfileEntityTitle(res));
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const stationId = data.stationId?.value;
    const inventoryMode = data.inventoryMode?.value ?? 1;
    delete data.station;
    delete data.stationId;
    delete data.inventoryMode;
    if (params?.id && !props?.setAddNew) {
      StoreProfileService.updateStore(+params?.id, {
        ...data,
        stationId,
        inventoryMode,
      })
          .then((response: any) => {
            if (response.success) {
              captureLabel(data?.title || pickProfileEntityTitle(response));
              navigate("/stores");
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("store") + " " + t("updated") + " " + t("successfully")
                  }
                />,
                { variant: "success" },
              );
            } else {
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
              setIsBtnEnabled(true);
            }
          })
          .catch((err: any) => {
            console.error("===", err);
            setIsBtnEnabled(true);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
          });
    } else {
      StoreProfileService.createStore({
        ...data,
        stationId,
        inventoryMode,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("store") + " " + t("created") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew("");
              props.refetch();
            } else navigate("/stores");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stations")
        stationsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          breadcrumbItems={breadcrumbItems}
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate("/stores")}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew ? t("editStore") : t("addStores")
          }
          icon={<Store height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_STORE_CREATE")}
        />
      )}
      <div className={`${params?.id && !props?.setAddNew ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <StoreTabs
            params={params}
            selectedIndex={0}
            inventoryMode={
              inventoryModeValue?.value ?? inventoryModeValue ?? null
            }
          />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id && !props?.setAddNew ? t("stores") : t("addStores")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            padding={!props?.setAddNew}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-baseline mb-2"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("storeName")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedStoreNamePlaceholder(primaryLanguage)}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("storeName")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("storeName")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedStoreNamePlaceholder(secondaryLanguage)}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("storeName")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("storeNumber", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={t("storeNumber") + " *"}
                    placeholder={t("phStoreNumber")}
                    title={t("storeNumber")}
                    fullWidth
                    error={!!errors.storeNumber}
                    helperText={errors.storeNumber?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="stationId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("station") + " *"}
                        {...field}
                        options={stations}
                        isClearable
                        isSearchable
                        defaultPlaceholder="phSelectStation"
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                        }}
                        error={errors.stationId?.message as string}
                        isLoading={searchLoading === "stations"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "stations",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("stations", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(stationsData?.pages[0]?.data as any).last)
                            handleDropdownActions("stations", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="inventoryMode"
                    control={control}
                    defaultValue={inventoryModeOptions[0]}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("inventoryMode") + " *"}
                        {...field}
                        options={inventoryModeOptions}
                        isClearable={false}
                        isSearchable={false}
                        defaultPlaceholder="phSelectInventoryMode"
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                        }}
                        error={errors.inventoryMode?.message as string}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
